import cx from "classnames";
import React, { useMemo, useState } from "react";
import { Button, Col, Form, FormFeedback, FormGroup, Input, Row } from "reactstrap";

import { TextBlock } from "../../../types";
import { extractFragmentFromBlock, insertFragmentIntoBlock } from "../util";

type Props = Readonly<{
  block: TextBlock;
  previewImage?: React.ReactElement;
  onCancel: () => void;
  onSave: (correction: string) => void;
}>;

export const Editing = ({ block, onCancel, onSave, previewImage }: Props) => {
  const initialState = useMemo(() => extractFragmentFromBlock(block.element), [block.element]);
  const [contentText, setContentText] = useState<string>(initialState);
  const [xmlValid, setXMLValid] = useState(true);
  return (
    <Form
      className="border bg-light p-3 mb-2"
      style={{
        width:"calc(100% + 50px)" // Buffer because the editor widget has a bunch of margins, make sure the text will fit
      }}
      onSubmit={e => {
        e.preventDefault();
        const newBlock = insertFragmentIntoBlock(block.element.cloneNode() as Element, contentText);
        onSave(newBlock.innerHTML);
      }}
    >
      <div>
        <Row>
          <Col>{previewImage}</Col>
        </Row>
        <FormGroup row className="mb-0">
          <Col>
            <Input
              autoFocus
              className="d-block mb-2"
              invalid={!xmlValid}
              onChange={e => {
                setContentText(e.target.value);
                try {
                  const clone = block.element.cloneNode() as Element;
                  insertFragmentIntoBlock(clone, e.target.value);
                  setXMLValid(true);
                } catch (err) {
                  setXMLValid(false);
                }
              }}
              onKeyDown={e => {
                e.stopPropagation();
                if (e.key === "Escape") {
                  onCancel();
                }
              }}
              rows={6}
              type="textarea"
              valid={xmlValid}
              value={contentText}
            />
          </Col>
        </FormGroup>
      </div>
      <div className="d-flex justify-content-between align-items-center">
        <Button color="secondary" onClick={onCancel} type="button">
          Cancel
        </Button>
        <FormFeedback
          className="d-block flex-fill text-center"
          invalid={!xmlValid}
          valid={xmlValid}
        >
          <i className={cx("fa", "mr-2", { "fa-check": xmlValid, "fa-times": !xmlValid })} />
          {xmlValid ? "XML is valid" : "XML is invalid"}
        </FormFeedback>
        <Button color="primary" disabled={!xmlValid} type="submit">
          Save
        </Button>
      </div>
    </Form>
  );
};
Editing.displayName = "Editing";
